import { create } from 'zustand';
import {
  createWorkerRuntime,
  createMainThreadRuntime,
  prefetchPoseAssets,
} from '../workers/pose-runtime';
import { createRemoteRuntime } from '../workers/remote-pose-runtime';
import { recordExerciseVideo } from '../utilis/exercise-video';
import {
  POSE_BACKEND,
  POSE_SERVICE_URL,
  POSE_STREAM_FPS,
  POSE_STREAM_WIDTH,
  POSE_TRANSPORT,
} from '../../config/features';

// Ordered fallback ladder. The store attempts each rung in turn and keeps the
// first that successfully builds a PoseLandmarker:
//
//   1. Worker + GPU  — best case (desktop, Android): inference off the main
//                      thread, hardware accelerated.
//   2. Worker + CPU  — worker context is fine but WebGL2-in-worker isn't.
//   3. Main + GPU    — worker context can't build the WASM graph at all (the
//                      iOS Safari "Can't find variable: document" failure), so
//                      run on the main thread where `document` exists.
//   4. Main + CPU    — final safety net.
//
// Because the whole ladder is walked before the user ever sees a failure, the
// "Couldn't prepare the exercise" screen only appears when even main-thread+CPU
// failed (a genuine hard failure — e.g. assets couldn't be fetched at all).
const STRATEGIES = [
  { kind: 'worker', delegate: 'GPU' },
  { kind: 'worker', delegate: 'CPU' },
  { kind: 'main', delegate: 'GPU' },
  { kind: 'main', delegate: 'CPU' },
];
// The remote backend (FEATURE_POSE_BACKEND=remote) is its own single rung: no MediaPipe
// download, no fallback to it — a session is either scored on the service's model or fails
// visibly, so the two paths are never mixed within one session.
const REMOTE_STRATEGIES = [{ kind: 'remote', delegate: 'GPU' }];

function sessionIdHint() {
  try {
    return JSON.parse(localStorage.getItem('initialConfig') || '{}')?.sessionId || '';
  } catch (error) {
    return '';
  }
}

// Module-scoped so a remount of any consumer reuses the single built runtime
// instead of rebuilding it.
let runtimeInstance = null;
let buildPromise = null;
let buildAbort = null; // cancels the in-flight build: LiveKit's can wait minutes in a room for a free place
let prefetchPromise = null;

// A runtime with a `sessionId` (LiveKit: that session's room) serves that session only.
const servesAnotherSession = runtime =>
  Boolean(runtime?.sessionId) && runtime.sessionId !== sessionIdHint();

async function buildRuntime(onProgress, signal) {
  const failures = [];

  const strategies = POSE_BACKEND === 'remote' ? REMOTE_STRATEGIES : STRATEGIES;
  for (const strategy of strategies) {
    onProgress({ phase: 'wasm', loaded: 0, total: 0 });
    try {
      let runtime;
      if (strategy.kind === 'remote') {
        const options = {
          sessionId: sessionIdHint(),
          onProgress,
          targetWidth: POSE_STREAM_WIDTH,
          targetFps: POSE_STREAM_FPS,
          recordVideo: recordExerciseVideo(sessionIdHint()),
          signal,
        };
        if (POSE_TRANSPORT === 'livekit') {
          // On demand: livekit-client stays out of the main bundle while WebRTC is the default.
          const { createLivekitRuntime } = await import('../workers/livekit-pose-runtime');
          runtime = await createLivekitRuntime(options);
        } else {
          runtime = await createRemoteRuntime({ ...options, url: POSE_SERVICE_URL });
        }
      } else if (strategy.kind === 'worker') {
        runtime = await createWorkerRuntime({ delegate: strategy.delegate, onProgress });
      } else {
        runtime = await createMainThreadRuntime({ delegate: strategy.delegate, onProgress });
      }
      return { runtime, strategy };
    } catch (error) {
      const label = `${strategy.kind}/${strategy.delegate}`;
      const message = error?.message || String(error);
      failures.push(`${label}: ${message}`);
      console.warn(`Pose runtime rung "${label}" failed, trying next.`, error);
    }
  }

  const aggregate = new Error(failures[failures.length - 1] || 'All pose runtimes failed to load');
  aggregate.details = failures;
  throw aggregate;
}

export const usePoseWorkerStore = create((set, get) => ({
  // The active pose runtime handle ({ kind, delegate, detect, warmup, dispose })
  // once a ladder rung succeeds — consumed by the exercise detection loop.
  runtime: null,
  // Which rung won, for diagnostics/telemetry (e.g. { kind:'main', delegate:'CPU' }).
  activeRuntime: null,
  isReady: false,
  error: null,
  // Real download progress. `phase` is 'wasm' while the WASM fileset loads (no
  // byte signal) then 'model' with real loaded/total.
  progress: { phase: 'wasm', loaded: 0, total: 0 },

  // App startup: only warm the model + WASM cache, do NOT build a PoseLandmarker
  // (which is expensive and can fail per-device). The exercise page builds the
  // real runtime later via ensureRuntime(). Idempotent + never throws.
  prefetchAssets() {
    if (prefetchPromise) {
      return prefetchPromise;
    }
    // The remote backend has nothing to download.
    prefetchPromise =
      POSE_BACKEND === 'remote' ? Promise.resolve() : prefetchPoseAssets().catch(() => {});
    return prefetchPromise;
  },

  // Exercise preparation: build a runtime by walking the fallback ladder. Safe
  // to call repeatedly — returns the in-flight build or the already-built
  // runtime.
  ensureRuntime() {
    if (servesAnotherSession(runtimeInstance)) {
      get().releaseSessionRuntime();
    }
    if (runtimeInstance) {
      return Promise.resolve(runtimeInstance);
    }
    if (buildPromise) {
      // A build for the previous session may still land: rebuild for this one.
      return buildPromise.then(runtime =>
        servesAnotherSession(runtime) ? get().ensureRuntime() : runtime
      );
    }

    set({ error: null, isReady: false, progress: { phase: 'wasm', loaded: 0, total: 0 } });

    const abort = new AbortController();
    buildAbort = abort;
    const onProgress = update =>
      !abort.signal.aborted &&
      set({
        progress: {
          phase: update.phase,
          loaded: update.loaded ?? 0,
          total: update.total ?? 0,
        },
      });

    // A cancelled build (releaseSessionRuntime) touches nothing: a newer one may be running.
    buildPromise = buildRuntime(onProgress, abort.signal)
      .then(({ runtime, strategy }) => {
        if (abort.signal.aborted) {
          runtime.dispose?.();
          return null;
        }
        runtimeInstance = runtime;
        set({ runtime, activeRuntime: strategy, isReady: true, error: null });
        return runtime;
      })
      .catch(error => {
        if (abort.signal.aborted) {
          return null;
        }
        buildPromise = null;
        set({
          runtime: null,
          activeRuntime: null,
          isReady: false,
          error: error?.message || 'Failed to load MediaPipe Pose',
        });
        return null;
      });

    return buildPromise;
  },

  // Re-attempt after a failure. Unlike the old retry (which re-ran the identical
  // failing strategy), this tears down any partial runtime and re-walks the
  // whole ladder from the top — so a transient failure (e.g. flaky asset fetch)
  // gets a fresh shot at every rung, and a device that can only run on the main
  // thread ends up there.
  retry() {
    if (runtimeInstance) {
      try {
        runtimeInstance.dispose?.();
      } catch (error) {
        // ignore teardown failures
      }
      runtimeInstance = null;
    }
    buildPromise = null;
    set({
      runtime: null,
      activeRuntime: null,
      error: null,
      isReady: false,
      progress: { phase: 'wasm', loaded: 0, total: 0 },
    });
    return get().ensureRuntime();
  },

  // Leaving the exercise: a session-bound runtime (LiveKit: the session's room, which costs
  // minutes and GPU while open) goes with it. MediaPipe and WebRTC runtimes stay warm.
  releaseSessionRuntime() {
    if (
      !runtimeInstance &&
      buildPromise &&
      POSE_BACKEND === 'remote' &&
      POSE_TRANSPORT === 'livekit'
    ) {
      // Still joining (maybe queued for a free place): leaving cancels it and leaves the room.
      buildAbort?.abort();
      buildPromise = null;
      set({ isReady: false });
      return;
    }
    if (!runtimeInstance?.sessionId) {
      return;
    }
    try {
      runtimeInstance.dispose?.();
    } catch (error) {
      // ignore teardown failures
    }
    runtimeInstance = null;
    buildPromise = null;
    set({ runtime: null, activeRuntime: null, isReady: false });
  },
}));
